/**
 * @module DialogContext
 * @description Contexte d'application pour afficher une seule modale de dialogue
 * (confirmation, information, erreur) de manière centralisée.
 *
 * @remarks
 * Objectifs:
 * - Fournir une API simple `openDialog(options)` / `closeDialog()` utilisable partout.
 * - Éviter la duplication de modales et la logique UI dans les composants métiers.
 * - Permettre la personnalisation (contenu, actions, props Mantine) sans coupler au domaine.
 *
 * @example
 * <DialogProvider>
 *   <AppRoutes />
 * </DialogProvider>
 *
 * const { openDialog } = useDialog();
 * openDialog({
 *   title: 'Confirmer la suppression',
 *   message: 'Cette action est irréversible.',
 *   variant: 'error',
 *   cancelLabel: 'Annuler',
 *   confirmLabel: 'Supprimer',
 *   onConfirm: () => deleteItem(id),
 * });
 *
 * @example
 * openDialog({
 *   title: 'Choix requis',
 *   content: <MyCustomContent />,
 *   actions: [
 *     { label: 'Plus tard', variant: 'default' },
 *     { label: 'Continuer', color: 'blue', onClick: proceed },
 *   ],
 *   modalProps: { size: 'lg' },
 * });
 */
'use client';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { ModalProps } from '@mantine/core';
import { Button, Flex, Modal, Text } from '@mantine/core';

/**
 * Type de variante pour le dialogue.
 * @typedef {'confirm' | 'info' | 'error'} DialogVariant
 */
type DialogVariant = 'confirm' | 'info' | 'error';

/**
 * Configuration d'une action personnalisée pour le dialogue.
 * @typedef {Object} DialogAction
 * @property {string} label - Label affiché sur le bouton
 * @property {string} [color] - Couleur du bouton (Mantine)
 * @property {'filled' | 'light' | 'outline' | 'subtle' | 'white' | 'default'} [variant] - Variante du bouton (Mantine)
 * @property {() => void} [onClick] - Callback déclenché au clic
 * @property {boolean} [autoClose=true] - Ferme automatiquement la modale après le clic
 * @property {boolean} [disabled] - Désactive le bouton
 * @property {boolean} [loading] - Affiche un état de chargement
 * @property {React.ReactNode} [leftSection] - Icône ou élément à gauche du label
 */
export type DialogAction = {
    label: string;
    color?: string;
    variant?: 'filled' | 'light' | 'outline' | 'subtle' | 'white' | 'default';
    onClick?: () => void;
    autoClose?: boolean;
    disabled?: boolean;
    loading?: boolean;
    leftSection?: React.ReactNode;
};

/**
 * Options de configuration pour un dialogue.
 * @typedef {Object} DialogOptions
 * @property {string} title - Titre du dialogue
 * @property {string | React.ReactNode} [message] - Contenu simple (texte ou JSX)
 * @property {React.ReactNode} [content] - Contenu personnalisé (remplace message si fourni)
 * @property {string} [confirmLabel] - Label du bouton de confirmation (mode hérité)
 * @property {string} [cancelLabel] - Label du bouton d'annulation (mode hérité)
 * @property {() => void} [onConfirm] - Callback de confirmation (mode hérité)
 * @property {() => void} [onCancel] - Callback d'annulation (mode hérité)
 * @property {() => void} [onClose] - Callback lors de la fermeture de la modale (croix/overlay/escape)
 * @property {() => void} [onCloseIcon] - Callback déclenché spécifiquement au clic sur la croix
 * @property {DialogAction[]} [actions] - Actions personnalisées (remplace confirm/cancel si fourni)
 * @property {DialogVariant} [variant] - Variante de dialogue ('confirm' | 'info' | 'error')
 * @property {Partial<ModalProps>} [modalProps] - Props Mantine Modal pour personnalisation avancée
 */
export type DialogOptions = {
    title: string;
    message?: string | React.ReactNode;
    content?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onClose?: () => void;
    onCloseIcon?: () => void;
    actions?: DialogAction[];
    variant?: DialogVariant;
    modalProps?: Partial<ModalProps>;
};

type DialogId = string;

type DialogContextValue = {
    openDialog: (options: DialogOptions) => DialogId;
    closeDialog: (id?: DialogId) => void;
    updateDialog: (
        id: DialogId,
        patch: Partial<DialogOptions> | ((prev: DialogOptions) => Partial<DialogOptions>),
    ) => void;
};

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

/**
 * Hook pour accéder au contexte de dialogue.
 * @returns {DialogContextValue} Objet contenant `openDialog` et `closeDialog`
 * @throws {Error} Si utilisé en dehors d'un `DialogProvider`
 * @example
 * const { openDialog, closeDialog } = useDialog();
 * openDialog({ title: 'Bonjour', message: 'Bienvenue!' });
 */
const useDialog = () => {
    const ctx = useContext(DialogContext);
    if (!ctx) throw new Error('useDialog must be used within DialogProvider');
    return ctx;
};

/**
 * Provider pour le contexte de dialogue.
 * Rend une modale Mantine unique, pilotée par `openDialog`/`closeDialog`.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Arbre de composants enfants
 * @example
 * <DialogProvider>
 *   <App />
 * </DialogProvider>
 */
const DialogProvider = ({ children }: { children: React.ReactNode }) => {
    type StackItem = { id: DialogId; options: DialogOptions };
    const [stack, setStack] = useState<StackItem[]>([]);

    const closeDialog = useCallback((id?: DialogId) => {
        setStack((prev) => {
            if (!prev.length) return prev;
            if (!id) return prev.slice(0, -1);
            return prev.filter((item) => item.id !== id);
        });
    }, []);

    const openDialog = useCallback((opts: DialogOptions) => {
        const id: DialogId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setStack((prev) => [...prev, { id, options: opts }]);
        return id;
    }, []);

    const updateDialog = useCallback(
        (
            id: DialogId,
            patch: Partial<DialogOptions> | ((prev: DialogOptions) => Partial<DialogOptions>),
        ) => {
            setStack((prev) =>
                prev.map((item) => {
                    if (item.id !== id) return item;
                    const nextPatch = typeof patch === 'function' ? patch(item.options) : patch;
                    return { ...item, options: { ...item.options, ...nextPatch } };
                }),
            );
        },
        [],
    );

    const value = useMemo(
        () => ({ openDialog, closeDialog, updateDialog }),
        [openDialog, closeDialog, updateDialog],
    );

    const renderActions = (item: StackItem) => {
        const options = item.options;
        if (options?.actions && options.actions.length > 0) {
            return options.actions.map((action, idx) => (
                <Button
                    key={idx}
                    color={action.color}
                    variant={action.variant}
                    disabled={action.disabled}
                    loading={action.loading}
                    // @ts-expect-error Cross-version ReactNode compatibility
                    leftSection={action.leftSection}
                    onClick={() => {
                        action.onClick?.();
                        if (action.autoClose !== false) closeDialog(item.id);
                    }}
                >
                    {action.label}
                </Button>
            ));
        }
        // Fallback compat: cancel + confirm
        return (
            <>
                {options?.cancelLabel !== undefined || options?.onCancel ? (
                    <Button
                        variant="default"
                        onClick={() => {
                            options?.onCancel?.();
                            closeDialog(item.id);
                        }}
                    >
                        {options?.cancelLabel ?? 'Annuler'}
                    </Button>
                ) : null}
                <Button
                    color={options?.variant === 'error' ? 'red' : 'blue'}
                    onClick={() => {
                        options?.onConfirm?.();
                        closeDialog(item.id);
                    }}
                >
                    {options?.confirmLabel ?? 'Confirmer'}
                </Button>
            </>
        );
    };

    const resolveContent = (item: StackItem) => {
        const options = item.options;
        return options?.content ?? (
            options?.message
                ? typeof options.message === 'string'
                    ? <Text>
                        {options.message}
                    </Text>
                    : options.message
                : null
        );
    };

    return (
        <DialogContext.Provider value={value}>
            {children}
            {stack.map((item, idx) => (
                <Modal
                    key={item.id}
                    opened
                    title={item.options?.title}
                    centered
                    zIndex={200 + idx * 10}
                    withinPortal
                    {
                        ...(() => {
                            // Fusionner proprement les closeButtonProps pour intercepter le clic sur la croix
                            const modalProps = item.options?.modalProps ?? {};
                            const currentCloseBtn = (modalProps as any).closeButtonProps ?? {};
                            // Utiliser une ref locale par item pour marquer l'origine si nécessaire
                            // Ici, on ne bloque pas le onClose mais on déclenche le callback spécifique directement
                            const onClickCross = (e: unknown) => {
                                try {
                                    currentCloseBtn?.onClick?.(e as never);
                                } catch {
                                    // ignore erreurs utilisateur
                                }
                                item.options?.onCloseIcon?.();
                                // Ne pas appeler closeDialog ici pour éviter un double close;
                                // Mantine déclenchera onClose juste après.
                            };
                            return {
                                ...(item.options?.modalProps ?? {}),
                                closeButtonProps: {
                                    ...currentCloseBtn,
                                    onClick: onClickCross,
                                },
                            } as Partial<ModalProps>;
                        })()
                    }
                    // Gestion fine de la fermeture: onClose (général) + détection clic croix
                    onClose={() => {
                        const opts = item.options;
                        // Déclenche le callback général
                        opts?.onClose?.();
                        // Si la fermeture a été initiée par la croix, le callback dédié est déjà déclenché via closeButtonProps
                        closeDialog(item.id);
                    }}
                >
                    { }
                    <>
                        {resolveContent(item) as any}
                    </>
                    <Flex mt="md" gap="sm" justify="end">
                        {renderActions(item)}
                    </Flex>
                </Modal>
            ))}
        </DialogContext.Provider>
    );
};

export {
    DialogProvider,
    useDialog,
};